import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Plus, Settings, LogOut, Bus as BusIcon, Wrench, Fuel, Wallet, ChevronDown, Menu, X, MapPin, Users, Calendar } from 'lucide-react';
import OnlineUsersIndicator from './OnlineUsersIndicator';
import BusCard from './BusCard';
import BusFormDialog from './BusFormDialog';
import SendToMechanicDialog from './SendToMechanicDialog';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useModules } from '@/contexts/ModulesContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
});

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isModuleEnabled } = useModules();
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  const [buses, setBuses] = useState([]);
  const [parameterTypes, setParameterTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBus, setEditingBus] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'fleet', 'personal', 'problem'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mechanicDialogOpen, setMechanicDialogOpen] = useState(false);
  const [mechanicBus, setMechanicBus] = useState(null);

  useEffect(() => {
    fetchBuses();
    fetchParameterTypes();
  }, []);

  const fetchBuses = async () => {
    try {
      // Load buses WITH thumbnails (small photos for cards)
      const response = await axios.get(`${API}/buses?thumbnails_only=true`, getAuthHeaders());
      setBuses(response.data);
    } catch (error) {
      toast.error('Ошибка загрузки автобусов');
    } finally {
      setLoading(false);
    }
  };

  // Function to check if bus has critical problems
  const isBusProblematic = (bus) => {
    if (!bus.parameters || bus.parameters.length === 0) return false;
    
    for (const param of bus.parameters) {
      const paramType = parameterTypes.find(pt => pt.id === param.parameter_type_id);
      
      // Only check critical parameters
      if (!paramType || paramType.is_critical === false) continue;
      
      const trackingType = param.tracking_type || 'date';
      
      if (trackingType === 'date' && param.end_date) {
        const daysUntilExpiry = Math.floor(
          (new Date(param.end_date) - new Date()) / (1000 * 60 * 60 * 24)
        );
        // Red zone: expired or expiring within 30 days
        if (daysUntilExpiry < 0 || daysUntilExpiry <= 30) return true;
      } else if (trackingType === 'mileage' && param.end_mileage) {
        const kmRemaining = param.end_mileage - (bus.mileage || 0);
        // Red zone: 1000 km or less
        if (kmRemaining <= 1000) return true;
      }
    }
    
    return false;
  };

  // Filter buses based on selected filter
  const filteredBuses = buses.filter(bus => {
    if (filter === 'all') return true;
    if (filter === 'fleet') return !bus.is_personal;
    if (filter === 'personal') return bus.is_personal;
    if (filter === 'problem') return isBusProblematic(bus);
    return true;
  });

  const fetchParameterTypes = async () => {
    try {
      const response = await axios.get(`${API}/parameter-types`, getAuthHeaders());
      setParameterTypes(response.data);
    } catch (error) {
      console.error('Ошибка загрузки типов параметров:', error);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.href = '/login';
  };

  const handleAddBus = () => {
    setEditingBus(null);
    setDialogOpen(true);
  };

  const handleEditBus = async (bus) => {
    // Load full bus data WITH photo when editing
    try {
      const response = await axios.get(`${API}/buses/${bus.id}`, getAuthHeaders());
      setEditingBus(response.data);
      setDialogOpen(true);
    } catch (error) {
      toast.error('Ошибка загрузки данных автобуса');
    }
  };

  const handleDeleteBus = async (busId) => {
    if (!window.confirm('Вы уверены, что хотите удалить этот автобус?')) return;

    try {
      await axios.delete(`${API}/buses/${busId}`, getAuthHeaders());
      toast.success('Автобус удален');
      fetchBuses();
    } catch (error) {
      toast.error('Ошибка удаления автобуса');
    }
  };

  const handleSaveBus = () => {
    setDialogOpen(false);
    fetchBuses();
  };

  const handleMoveBus = async (busId, direction) => {
    try {
      await axios.post(`${API}/buses/${busId}/move?direction=${direction}`, {}, getAuthHeaders());
      fetchBuses(); // Refresh to show new order
    } catch (error) {
      if (error.response?.data?.detail === "Bus is already at the edge") {
        // Silently ignore - bus is at top or bottom
        return;
      }
      toast.error('Ошибка изменения порядка');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-lg">Загрузка...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-200 to-blue-100" data-testid="dashboard-page" style={{position: 'relative', zIndex: 1}}>
      {/* Modern Header with gradient */}
      <header className="sticky top-0 z-50 bg-gradient-to-r from-slate-800 via-slate-900 to-slate-800 shadow-xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo & Brand */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-amber-400 to-orange-500 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/30">
                <BusIcon className="w-6 h-6 text-white" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-lg font-bold text-white">BIGBUS</h1>
                <p className="text-xs text-slate-400">Привет, {user?.username || 'Пользователь'}! 👋</p>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden md:flex items-center gap-1">
              {isModuleEnabled('bus_management') && (
                <Button
                  variant="ghost"
                  onClick={handleAddBus}
                  className="text-slate-300 hover:text-green-400 hover:bg-green-500/10"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Добавить
                </Button>
              )}
              {isModuleEnabled('refueling') && (
                <Button
                  variant="ghost"
                  onClick={() => navigate('/fuel')}
                  className="text-slate-300 hover:text-orange-400 hover:bg-orange-500/10"
                >
                  <Fuel className="w-4 h-4 mr-2" />
                  Топливо
                </Button>
              )}
              {isModuleEnabled('trips') && (
                <Button
                  variant="ghost"
                  onClick={() => navigate('/trips')}
                  className="text-slate-300 hover:text-blue-400 hover:bg-blue-500/10"
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Рейсы
                </Button>
              )}
              {isModuleEnabled('mechanic') && (
                <Button
                  variant="ghost"
                  onClick={() => navigate('/mechanic')}
                  className="text-slate-300 hover:text-purple-400 hover:bg-purple-500/10"
                >
                  <Wrench className="w-4 h-4 mr-2" />
                  Механик
                </Button>
              )}
              {isModuleEnabled('employee_reports') && (
                <Button
                  variant="ghost"
                  onClick={() => navigate('/employee-reports')}
                  className="text-slate-300 hover:text-blue-400 hover:bg-blue-500/10"
                >
                  <Users className="w-4 h-4 mr-2" />
                  Отчёты
                </Button>
              )}
              {isAdmin && (
                <Button
                  variant="ghost"
                  onClick={() => navigate('/calendar')}
                  className="text-slate-300 hover:text-cyan-400 hover:bg-cyan-500/10"
                  data-testid="nav-calendar-button"
                >
                  <Calendar className="w-4 h-4 mr-2" />
                  Календарь
                </Button>
              )}
              <div className="w-px h-6 bg-slate-700 mx-2" />
              <Button
                variant="ghost"
                onClick={() => navigate('/admin')}
                className="text-slate-300 hover:text-white hover:bg-slate-700"
              >
                <Settings className="w-4 h-4 mr-2" />
                Настройки
              </Button>
            </nav>

            {/* Right Section */}
            <div className="flex items-center gap-3">
              <OnlineUsersIndicator />
              
              {/* Desktop Logout */}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                className="hidden md:flex text-slate-400 hover:text-red-400 hover:bg-red-500/10"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Выход
              </Button>

              {/* Mobile Menu Button */}
              <Button
                variant="ghost"
                size="sm"
                className="md:hidden text-slate-300 hover:text-white hover:bg-slate-700"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
              </Button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-slate-700 bg-slate-800 pb-4">
            <div className="px-4 pt-2 space-y-1">
              <p className="px-3 py-2 text-sm text-slate-400">
                👋 Привет, {user?.username || 'Пользователь'}!
              </p>
              {isModuleEnabled('bus_management') && (
                <button
                  onClick={() => { handleAddBus(); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-slate-300 hover:bg-green-500/10 hover:text-green-400 rounded-lg transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  <span className="font-medium">Добавить автобус</span>
                </button>
              )}
              {isModuleEnabled('refueling') && (
                <button
                  onClick={() => { navigate('/fuel'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-slate-300 hover:bg-orange-500/10 hover:text-orange-400 rounded-lg transition-colors"
                >
                  <Fuel className="w-5 h-5" />
                  <span className="font-medium">Топливо</span>
                </button>
              )}
              {isModuleEnabled('trips') && (
                <button
                  onClick={() => { navigate('/trips'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-slate-300 hover:bg-blue-500/10 hover:text-blue-400 rounded-lg transition-colors"
                >
                  <MapPin className="w-5 h-5" />
                  <span className="font-medium">Рейсы</span>
                </button>
              )}
              {isModuleEnabled('mechanic') && (
                <button
                  onClick={() => { navigate('/mechanic'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-slate-300 hover:bg-purple-500/10 hover:text-purple-400 rounded-lg transition-colors"
                >
                  <Wrench className="w-5 h-5" />
                  <span className="font-medium">Механик</span>
                </button>
              )}
              {isModuleEnabled('employee_reports') && (
                <button
                  onClick={() => { navigate('/employee-reports'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-slate-300 hover:bg-blue-500/10 hover:text-blue-400 rounded-lg transition-colors"
                >
                  <Users className="w-5 h-5" />
                  <span className="font-medium">Отчёты</span>
                </button>
              )}
              {isAdmin && (
                <button
                  onClick={() => { navigate('/calendar'); setMobileMenuOpen(false); }}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-slate-300 hover:bg-cyan-500/10 hover:text-cyan-400 rounded-lg transition-colors"
                  data-testid="nav-calendar-button-mobile"
                >
                  <Calendar className="w-5 h-5" />
                  <span className="font-medium">Календарь</span>
                </button>
              )}
              <div className="my-2 border-t border-slate-700" />
              <button
                onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
              >
                <Settings className="w-5 h-5" />
                <span className="font-medium">Настройки</span>
              </button>
              <button
                onClick={() => { handleLogout(); setMobileMenuOpen(false); }}
                className="w-full flex items-center gap-3 px-3 py-2.5 text-left text-red-400 hover:bg-red-500/10 rounded-lg transition-colors"
              >
                <LogOut className="w-5 h-5" />
                <span className="font-medium">Выход</span>
              </button>
            </div>
          </div>
        )}
      </header>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Filter buttons */}
        {buses.length > 0 && (
          <div className="mb-6 flex gap-2 flex-wrap">
            <Button
              onClick={() => setFilter('all')}
              variant={filter === 'all' ? 'default' : 'outline'}
              className={`${
                filter === 'all'
                  ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              } font-semibold`}
            >
              Все ({buses.length})
            </Button>
            <Button
              onClick={() => setFilter('fleet')}
              variant={filter === 'fleet' ? 'default' : 'outline'}
              className={`${
                filter === 'fleet'
                  ? 'bg-gradient-to-r from-green-500 to-green-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              } font-semibold`}
            >
              Автопарк ({buses.filter(b => !b.is_personal).length})
            </Button>
            <Button
              onClick={() => setFilter('personal')}
              variant={filter === 'personal' ? 'default' : 'outline'}
              className={`${
                filter === 'personal'
                  ? 'bg-gradient-to-r from-yellow-500 to-orange-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              } font-semibold`}
            >
              Личные ({buses.filter(b => b.is_personal).length})
            </Button>
          </div>
        )}

        {buses.length === 0 ? (
          <Card className="glass-effect shadow-2xl border-2 border-blue-200">
            <CardContent className="py-20 text-center">
              <div className="relative inline-block mb-6">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full blur-2xl opacity-30 animate-pulse"></div>
                <BusIcon className="relative w-24 h-24 text-blue-500 mx-auto" strokeWidth={1.5} />
              </div>
              <h3 className="text-3xl font-bold text-gray-800 mb-3">Автобусов пока нет</h3>
              <p className="text-lg text-gray-600 mb-8 max-w-md mx-auto">Начните управление вашим автопарком - добавьте первый автобус</p>
              <Button
                data-testid="empty-state-add-button"
                onClick={handleAddBus}
                className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all px-8 py-6 text-lg"
              >
                <Plus className="w-6 h-6 mr-3" strokeWidth={3} />
                Добавить первый автобус
              </Button>
            </CardContent>
          </Card>
        ) : filteredBuses.length === 0 ? (
          <Card className="glass-effect shadow-lg border-2 border-yellow-200">
            <CardContent className="py-12 text-center">
              <BusIcon className="w-16 h-16 text-yellow-500 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="text-2xl font-bold text-gray-800 mb-2">
                {filter === 'personal' ? 'Личных автомобилей нет' : 'Автомобилей автопарка нет'}
              </h3>
              <p className="text-gray-600">
                {filter === 'personal' 
                  ? 'Добавьте личные автомобили, отметив их чекбоксом при создании' 
                  : 'Автомобили автопарка не найдены'
                }
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {filteredBuses.map((bus, index) => (
              <BusCard
                key={bus.id}
                bus={bus}
                parameterTypes={parameterTypes}
                onEdit={isModuleEnabled('bus_management') ? handleEditBus : null}
                onDelete={isModuleEnabled('bus_management') ? handleDeleteBus : null}
                onMoveUp={() => handleMoveBus(bus.id, 'up')}
                onMoveDown={() => handleMoveBus(bus.id, 'down')}
                isFirst={index === 0}
                isLast={index === filteredBuses.length - 1}
                onSendToMechanic={isModuleEnabled('mechanic') ? (b) => { setMechanicBus(b); setMechanicDialogOpen(true); } : null}
              />
            ))}
          </div>
        )}
      </div>

      <BusFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        bus={editingBus}
        onSave={handleSaveBus}
      />

      <SendToMechanicDialog
        open={mechanicDialogOpen}
        onOpenChange={setMechanicDialogOpen}
        busId={mechanicBus?.id}
        busName={mechanicBus ? `${mechanicBus.brand} ${mechanicBus.model}` : ''}
      />

    </div>
  );
}